/* Plantillas listas de FiboSheets: «Nueva desde plantilla».
 *
 * ── PARA QUÉ ───────────────────────────────────────────────────────────────
 *
 * Una planilla en blanco asusta: hay que saber qué columnas poner y escribir
 * las fórmulas. Estas son las cuentas que una transportista hace todas las
 * semanas (cuánto cuesta un viaje, cuánta urea gasta cada interno, qué vence),
 * ya armadas: encabezados, fórmulas y un par de filas de ejemplo para ver cómo
 * se usa. Se crean como una planilla NUEVA del usuario: es suya, la cambia
 * como quiera y la plantilla no se entera.
 *
 * ── EL FORMATO ─────────────────────────────────────────────────────────────
 *
 * `libroDePlantilla(p)` devuelve exactamente lo que guarda Mi espacio
 * (`empaquetarLibro` en routes/MiEspacio.jsx): `{ hojas: [{ nombre, filas,
 * cols, celdas, formato, anchos, altos, fija }], activa }`, con la grilla
 * rectangular y del mismo tamaño que una planilla nueva (100 × 26) para que
 * haya lugar para seguir cargando. El formato va por referencia ("B4" →
 * `{ bold, bg, num… }`) y los anchos por índice de columna, como allá.
 *
 * ── LAS FÓRMULAS ───────────────────────────────────────────────────────────
 *
 * Sólo lo que el motor (`shared/planilla.js`) ya entiende, incluidas las
 * HUB.… de `shared/planillaHub.js` con sus argumentos tal cual. Las columnas
 * calculadas vienen copiadas hacia abajo en filas vacías, envueltas en
 * =SI(A5="";"";…): así quien carga un interno nuevo ve la patente sin tener
 * que arrastrar nada, y las filas vacías no muestran errores ni ceros.
 *
 * Los ejemplos son genéricos: ningún nombre de persona. Los internos son los
 * que usa de ejemplo la ayuda de las fórmulas (10) y otros inventados: si no
 * existen en el padrón, la celda dice #N/D y al pasar el mouse explica por qué.
 *
 * Cada plantilla se evalúa entera en `plantillasPlanilla.test.js`: ninguna
 * celda puede dar error y los totales tienen que dar lo que dicen.
 */

import { matrizVacia, parseRef, refDe } from "./planilla";

/* Las mismas medidas que una planilla nueva (FILAS_NUEVA y COLS_NUEVA en
   MiEspacio), así una plantilla se siente igual que «+ Nueva planilla». */
const FILAS = 100;
const COLS = 26;

/* Estilos de la paleta de Mi espacio (FONDOS): pasteles que se leen en los dos
   temas. Amarillo = «esto lo cambiás vos»; verde = el resultado. */
const TITULO = { bold: true };
const ENCABEZADO = { bold: true, bg: "#e0e0e0" };
const DATO = { bg: "#fff3b0" };
const RESULTADO = { bold: true, bg: "#c8e6c9" };
const NOTA = { italic: true, color: "#555555" };
const MONEDA = { num: "moneda" };

/* Aplica un estilo a un rango ("B4", "B4:B11") sumándolo a lo que ya tenía:
   una celda puede ser dato amarillo Y moneda. */
function estilo(formato, rango, extra) {
  const [a, b] = rango.split(":");
  const p = parseRef(a);
  const q = parseRef(b || a);
  for (let r = Math.min(p.fila, q.fila); r <= Math.max(p.fila, q.fila); r += 1) {
    for (let c = Math.min(p.col, q.col); c <= Math.max(p.col, q.col); c += 1) {
      const ref = refDe(r, c);
      formato[ref] = { ...(formato[ref] || {}), ...extra };
    }
  }
  return formato;
}

/* Las filas `desde`..`hasta` (numeradas como en la hoja, desde 1) armadas por
   `fila(n)`. Para las columnas calculadas que vienen copiadas hacia abajo. */
function filasDe(desde, hasta, fila) {
  const out = [];
  for (let n = desde; n <= hasta; n += 1) out.push(fila(n));
  return out;
}

/* Pisa, en las filas generadas, las celdas de ejemplo (`{ fila: [valores] }`,
   fila como en la hoja). Un valor `undefined` deja la fórmula que había. */
function conEjemplos(filas, desde, ejemplos) {
  return filas.map((f, i) => {
    const ej = ejemplos[desde + i];
    return ej ? f.map((v, c) => (ej[c] === undefined ? v : ej[c])) : f;
  });
}

/* ── Las plantillas ──────────────────────────────────────────────────────── */

function costoDeViaje() {
  const celdas = [
    ["Costo de viaje"],
    ["Cambiá lo amarillo; lo demás se calcula solo."],
    ["Dato", "Valor", "Nota"],
    ["Kilómetros del viaje", "3100", "Ida y vuelta, si el camión vuelve vacío."],
    ["Consumo (litros cada 100 km)", "38", "Lo que gasta la unidad cargada."],
    ["Precio del gasoil ($/litro)", "=HUB.PRECIO_GASOIL()", "El último precio declarado (lo que pagamos). Si dice #HUB (sin permiso de YPF), escribí el precio a mano."],
    ["Litros de gasoil", "=B4*B5/100"],
    ["Gasoil ($)", "=B7*B6"],
    ["Peajes ($)", "45000"],
    ["Viáticos del chofer ($)", "120000"],
    ["Otros gastos ($)", "0", "Cruce, balsa, comidas: lo que haga falta."],
    ["Total del viaje ($)", "=SUMA(B8:B11)"],
    ["Costo por km ($)", "=SI(B4>0;REDONDEAR(B12/B4;2);0)"],
  ];
  const f = {};
  estilo(f, "A1", TITULO);
  estilo(f, "A2", NOTA);
  estilo(f, "A3:C3", ENCABEZADO);
  estilo(f, "B4:B5", DATO);
  estilo(f, "B9:B11", DATO);
  estilo(f, "B6", MONEDA);
  estilo(f, "B8:B13", MONEDA);
  estilo(f, "A12:B13", RESULTADO);
  estilo(f, "C4:C11", NOTA);
  return { nombre: "Costo", celdas, formato: f, anchos: { 0: 230, 1: 130, 2: 420 } };
}

function controlDeUrea() {
  // Filas 2 a 21 listas para cargar; la 23 suma.
  const filas = filasDe(2, 21, (n) => [
    "",
    `=SI(A${n}="";"";HUB.VEHICULO(A${n};"patente"))`,
    "",
    "",
    `=SI(Y(A${n}<>"";D${n}>0);REDONDEAR(C${n}/D${n}*100;2);"")`,
  ]);
  const celdas = [
    ["Interno", "Patente", "Litros de urea cargados", "Km recorridos", "Urea cada 100 km", "", "Cómo se usa"],
    ...conEjemplos(filas, 2, {
      2: ["10", undefined, "180", "9500"],
      3: ["812", undefined, "150", "7800"],
      4: ["903", undefined, "210", "10200"],
    }),
    [],
    ["Total", "", "=SUMA(C2:C21)", "=SUMA(D2:D21)", '=SI(D23>0;REDONDEAR(C23/D23*100;2);"")'],
  ];
  // Las notas al costado de la tabla, fuera de las columnas que se suman.
  celdas[1][6] = "Escribí el interno (con F adelante si es furgón): la patente la trae el hub.";
  celdas[2][6] = "Cargá los litros de urea y los km del mismo período (por ejemplo, el mes).";
  celdas[3][6] = "Suele andar entre el 3 % y el 6 % de lo que gasta de gasoil: si un interno se dispara, revisalo.";
  const f = {};
  estilo(f, "A1:E1", ENCABEZADO);
  estilo(f, "G1", ENCABEZADO);
  estilo(f, "A2:A21", DATO);
  estilo(f, "C2:D21", DATO);
  estilo(f, "A23:E23", RESULTADO);
  estilo(f, "G2:G4", NOTA);
  return {
    nombre: "Urea",
    celdas,
    formato: f,
    anchos: { 0: 80, 1: 110, 2: 170, 3: 120, 4: 140, 5: 24, 6: 460 },
    fija: { fila: true },
  };
}

function viajesDelMes() {
  // B3/B4 son el mes elegido y B5/B6 el anterior: las fórmulas de la tabla
  // sólo los nombran, así cambiar de mes es cambiar una sola celda (B2).
  // Las filas de un cliente le pasan el cliente (columna A) y quedan vacías
  // mientras no haya uno escrito; las de «Todos» no lo pasan.
  const cuenta = (n, conCliente) => {
    const viajes = (desde, hasta) => (conCliente
      ? `=SI(A${n}="";"";HUB.VIAJES(${desde};${hasta};B${n};A${n}))`
      : `=HUB.VIAJES(${desde};${hasta};B${n})`);
    const parte = `SI($C$9>0;C${n}/$C$9;"")`;
    return [
      viajes("$B$3", "$B$4"),
      viajes("$B$5", "$B$6"),
      conCliente ? `=SI(A${n}="";"";${parte})` : `=${parte}`,
    ];
  };
  const porCliente = filasDe(13, 32, (n) => ["", "", ...cuenta(n, true)]);
  const celdas = [
    ["Viajes del mes"],
    ["Mes (cualquier día del mes)", "=HOY()", "Con HOY() es el mes en curso; escribí una fecha (01/09/2026) para ver otro."],
    ["Desde", "=FECHA(AÑO(B2);MES(B2);1)"],
    ["Hasta", "=FIN.MES(B2;0)"],
    ["Desde (mes anterior)", "=FECHA.MES(B3;-1)"],
    ["Hasta (mes anterior)", "=FIN.MES(B3;-1)"],
    [],
    ["Cliente", "Sentido", "Viajes", "Mes anterior", "% del mes"],
    ["Todos los clientes", "", ...cuenta(9, false)],
    ["Todos los clientes", "sur", ...cuenta(10, false)],
    ["Todos los clientes", "norte", ...cuenta(11, false)],
    ["Por cliente", "", "", "", "", "", "Escribí el cliente como figura en Presidencia. Sentido: sur, norte o vacío (los dos)."],
    ...conEjemplos(porCliente, 13, {
      13: ["Cliente A", "sur"],
      14: ["Cliente B", ""],
    }),
  ];
  const f = {};
  estilo(f, "A1", TITULO);
  estilo(f, "B2", DATO);
  estilo(f, "C2", NOTA);
  estilo(f, "A8:E8", ENCABEZADO);
  estilo(f, "A9:E9", RESULTADO);
  estilo(f, "A12", TITULO);
  estilo(f, "G12", NOTA);
  estilo(f, "A13:B32", DATO);
  estilo(f, "E9:E32", { num: "pct" });
  return { nombre: "Viajes", celdas, formato: f, anchos: { 0: 200, 1: 110, 2: 90, 3: 110, 4: 90, 5: 24, 6: 420 } };
}

function vencimientos() {
  const filas = filasDe(2, 31, (n) => [
    "",
    `=SI(A${n}="";"";HUB.VEHICULO(A${n};"patente"))`,
    "",
    "",
    `=SI(D${n}="";"";D${n}-HOY())`,
    `=SI(D${n}="";"";SI(E${n}<0;"VENCIDA";SI(E${n}<=30;"Vence pronto";"Al día")))`,
  ]);
  const celdas = [
    ["Interno", "Patente", "Documento", "Vence", "Días que faltan", "Estado", "", "Resumen", ""],
    // Las fechas de ejemplo se corren con HOY() para que la plantilla muestre
    // siempre los tres estados, la abran cuando la abran.
    ...conEjemplos(filas, 2, {
      2: ["10", undefined, "RTO", "=FECHA.SUMA(HOY();-5)"],
      3: ["F205", undefined, "RTO", "=FECHA.SUMA(HOY();12)"],
      4: ["812", undefined, "Seguro", "=FECHA.SUMA(HOY();95)"],
    }),
  ];
  // El resumen va en H:I, a un costado de la tabla (G queda de separador).
  const resumen = (fila, etiqueta, formula) => { celdas[fila - 1][7] = etiqueta; celdas[fila - 1][8] = formula; };
  resumen(2, "Vencidas", '=CONTAR.SI(F2:F31;"VENCIDA")');
  resumen(3, "Vencen en 30 días", '=CONTAR.SI(F2:F31;"Vence pronto")');
  resumen(4, "Al día", '=CONTAR.SI(F2:F31;"Al día")');
  celdas[5][7] = "Las fechas de ejemplo salen de HOY() para que veas los tres estados: escribí las reales (DD/MM/AAAA).";
  const f = {};
  estilo(f, "A1:F1", ENCABEZADO);
  estilo(f, "H1:I1", ENCABEZADO);
  estilo(f, "A2:A31", DATO);
  estilo(f, "C2:D31", DATO);
  estilo(f, "H2:I2", { bold: true, bg: "#f8bbd0" });
  estilo(f, "H3:I3", { bold: true, bg: "#ffe0b2" });
  estilo(f, "H4:I4", RESULTADO);
  estilo(f, "H6", NOTA);
  return {
    nombre: "Vencimientos",
    celdas,
    formato: f,
    anchos: { 0: 80, 1: 110, 2: 150, 3: 110, 4: 120, 5: 120, 6: 24, 7: 150, 8: 60 },
    fija: { fila: true },
  };
}

function viaticosDeChofer() {
  const filas = filasDe(6, 25, (n) => [
    "",
    "",
    "",
    `=SI(O(A${n}="";B${n}="");"";B${n}-A${n}+1)`,
    `=SI(D${n}="";"";D${n}*$B$3)`,
    "",
    `=SI(D${n}="";"";E${n}+F${n})`,
  ]);
  const celdas = [
    ["Liquidación de viáticos"],
    ["Chofer", "", "← nombre y apellido"],
    ["Viático por día ($)", "25000"],
    [],
    ["Salida", "Regreso", "Destino", "Días", "Viático ($)", "Gastos con comprobante ($)", "Total ($)"],
    ...conEjemplos(filas, 6, {
      6: ["06/10/2026", "09/10/2026", "Buenos Aires", undefined, undefined, "18000"],
      7: ["14/10/2026", "15/10/2026", "Río Gallegos"],
    }),
    [],
    ["Total", "", "", "=SUMA(D6:D25)", "=SUMA(E6:E25)", "=SUMA(F6:F25)", "=SUMA(G6:G25)"],
    ["Adelantos entregados ($)", "", "", "", "", "", "50000"],
    ["Saldo a pagar ($)", "", "", "", "", "", "=G27-G28"],
  ];
  const f = {};
  estilo(f, "A1", TITULO);
  estilo(f, "B2:B3", DATO);
  estilo(f, "C2", NOTA);
  estilo(f, "B3", MONEDA);
  estilo(f, "A5:G5", ENCABEZADO);
  estilo(f, "A6:C25", DATO);
  estilo(f, "F6:F25", DATO);
  estilo(f, "E6:G29", MONEDA);
  estilo(f, "A27:G27", RESULTADO);
  estilo(f, "G28", DATO);
  estilo(f, "A29:G29", RESULTADO);
  return { nombre: "Viáticos", celdas, formato: f, anchos: { 0: 170, 1: 100, 2: 140, 3: 60, 4: 110, 5: 190, 6: 120 } };
}

function presupuestoDeFlete() {
  const celdas = [
    ["Presupuesto de flete"],
    ["Cambiá lo amarillo; el precio sale solo."],
    ["Cliente", ""],
    ["Origen", "Río Grande"],
    ["Destino", "Buenos Aires"],
    ["Km de ida", "3100"],
    ["¿Vuelve vacío? (SI/NO)", "SI", "Si vuelve vacío, se cobran también los km de vuelta."],
    ["Km a cobrar", '=SI(MAYUSC(B7)="SI";B6*2;B6)'],
    ["Consumo (litros cada 100 km)", "38"],
    ["Precio del gasoil ($/litro)", "=HUB.PRECIO_GASOIL()", "Si dice #HUB (sin permiso de YPF), escribí el precio a mano."],
    [],
    ["Costos", "($)"],
    ["Gasoil", "=B8*B9/100*B10"],
    ["Peajes", "90000"],
    ["Viáticos del chofer", "200000"],
    ["Cruce, balsa y aduana", "0"],
    ["Otros", "0"],
    ["Costo total", "=SUMA(B13:B17)"],
    [],
    ["Margen (%)", "25", "Sobre el costo: 25 es un 25 %."],
    ["Precio sin IVA", "=REDONDEAR(B18*(1+B20/100);0)"],
    ["IVA (21 %)", "=B21*21%"],
    ["Precio final con IVA", "=B21+B22"],
    ["Precio por km (sin IVA)", "=SI(B8>0;REDONDEAR(B21/B8;2);0)"],
  ];
  const f = {};
  estilo(f, "A1", TITULO);
  estilo(f, "A2", NOTA);
  estilo(f, "B3:B7", DATO);
  estilo(f, "B9", DATO);
  estilo(f, "B10", MONEDA);
  estilo(f, "A12:B12", ENCABEZADO);
  estilo(f, "B14:B17", DATO);
  estilo(f, "B13:B18", MONEDA);
  estilo(f, "A18:B18", { bold: true });
  estilo(f, "B20", DATO);
  estilo(f, "B21:B24", MONEDA);
  estilo(f, "A21:B24", RESULTADO);
  estilo(f, "C7", NOTA);
  estilo(f, "C10", NOTA);
  estilo(f, "C20", NOTA);
  return { nombre: "Presupuesto", celdas, formato: f, anchos: { 0: 230, 1: 140, 2: 380 } };
}

/* El catálogo, en el orden en que se muestra. `usaHub` dice qué datos vivos
   trae, para avisar en la tarjeta que hace falta ese permiso. */
export const PLANTILLAS = [
  {
    id: "costo-viaje",
    nombre: "Costo de viaje",
    icono: "⛽",
    descripcion: "Cuánto sale un viaje: gasoil (con el precio del día), peajes y viáticos. Da el total y el costo por km.",
    usaHub: ["precio del gasoil"],
    armar: costoDeViaje,
  },
  {
    id: "urea",
    nombre: "Control de urea por interno",
    icono: "💧",
    descripcion: "Litros de urea y km de cada interno, con la patente del padrón y el consumo cada 100 km.",
    usaHub: ["padrón de la flota"],
    armar: controlDeUrea,
  },
  {
    id: "viajes-mes",
    nombre: "Viajes del mes por cliente",
    icono: "🚚",
    descripcion: "Cuántos viajes hubo este mes y el anterior: total, hacia el sur, hacia el norte y por cliente.",
    usaHub: ["viajes de Presidencia"],
    armar: viajesDelMes,
  },
  {
    id: "vencimientos",
    nombre: "Vencimientos RTO / documentación",
    icono: "📅",
    descripcion: "RTO, seguros y papeles de cada unidad: cuántos días faltan y cuáles están vencidos o por vencer.",
    usaHub: ["padrón de la flota"],
    armar: vencimientos,
  },
  {
    id: "viaticos",
    nombre: "Liquidación de viáticos de chofer",
    icono: "🧾",
    descripcion: "Días fuera por viaje, viático por día y gastos con comprobante, menos los adelantos: el saldo a pagar.",
    usaHub: [],
    armar: viaticosDeChofer,
  },
  {
    id: "presupuesto-flete",
    nombre: "Presupuesto de flete",
    icono: "💲",
    descripcion: "Del costo al precio: km, gasoil, peajes y viáticos, más el margen y el IVA. Da el precio final y por km.",
    usaHub: ["precio del gasoil"],
    armar: presupuestoDeFlete,
  },
];

/* Una plantilla → el libro tal cual lo guarda Mi espacio. La grilla queda
   rectangular (todas las filas con todas las columnas, en texto) y con lugar
   de sobra para seguir cargando. */
export function libroDePlantilla(plantilla) {
  const h = plantilla.armar();
  const filas = Math.max(FILAS, h.celdas.length + 20);
  const cols = Math.max(COLS, ...h.celdas.map((f) => f.length));
  const celdas = matrizVacia(filas, cols);
  h.celdas.forEach((f, r) => f.forEach((v, c) => { celdas[r][c] = v == null ? "" : String(v); }));
  return {
    hojas: [{
      nombre: h.nombre,
      filas,
      cols,
      celdas,
      formato: h.formato || {},
      anchos: h.anchos || {},
      altos: {},
      fija: h.fija || {},
    }],
    activa: 0,
  };
}
